import React from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { LuCircleAlert } from "react-icons/lu";
import CountryProvinceSelector from "../../../components/CountryProvinceSelector";
import {
  getCanadianDriverLicenseRule,
  normalizeCanadianDriverLicenseNumber,
  validateCanadianDriverLicense,
} from "../../../utils/canadianDriverLicenseRules";

const Step3License = ({
  form,
  handleChange,
  nextStep,
  prevStep,
  countries,
  provinces,
  classes,
  conditions,
}) => {
  const [classInputValue, setClassInputValue] = React.useState("");
  const [conditionInputValue, setConditionInputValue] = React.useState("");
  const [classTextValue, setClassTextValue] = React.useState(
    (form.dl_class || []).join(",")
  );
  const [conditionTextValue, setConditionTextValue] = React.useState(
    (form.dl_condition || []).join(",")
  );

  const buildSelectedOptions = (values = []) =>
    values.map((value) => ({ value, label: value }));

  const mergeOptions = (baseOptions, selectedValues = []) => {
    const optionMap = new Map(
      (baseOptions || []).map((option) => [option.value, option])
    );

    selectedValues.forEach((value) => {
      if (!optionMap.has(value)) {
        optionMap.set(value, { value, label: value });
      }
    });

    return Array.from(optionMap.values());
  };

  const pushMultiValue = (fieldName, selectedValues, rawValue, clearInput) => {
    const nextValue = rawValue.trim();
    if (!nextValue) return;

    const merged = Array.from(new Set([...(selectedValues || []), nextValue]));
    handleChange({
      target: {
        name: fieldName,
        value: merged,
      },
    });
    clearInput("");
  };

  const handleTokenKeyDown =
    (fieldName, selectedValues, inputValue, clearInput) => (event) => {
      if (!inputValue.trim()) return;

      if (event.key === "," || event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        pushMultiValue(fieldName, selectedValues, inputValue, clearInput);
      }
    };

  React.useEffect(() => {
    setClassTextValue((form.dl_class || []).join(","));
  }, [form.dl_class]);

  React.useEffect(() => {
    setConditionTextValue((form.dl_condition || []).join(","));
  }, [form.dl_condition]);

  const parseCommaSeparated = (value) =>
    value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const commitTextArray = (fieldName, rawValue) => {
    handleChange({
      target: {
        name: fieldName,
        value: parseCommaSeparated(rawValue),
      },
    });
  };

  const handleMobileDatePointerDown = (event) => {
    if (!window.matchMedia("(max-width: 768px)").matches) return;
    if (document.activeElement === event.currentTarget) return;

    event.preventDefault();
    const input = event.currentTarget;

    input.scrollIntoView({
      block: "end",
      behavior: "smooth",
    });

    setTimeout(() => {
      input.focus();
      if (typeof input.showPicker === "function") {
        input.showPicker();
      } else {
        input.click();
      }
    }, 250);
  };

  // ✅ Keep your handleSubmit
  const handleSubmit = (e) => {
    e.preventDefault();

    const validation = validateCanadianDriverLicense({
      country: form.dl_country,
      province: form.dl_province,
      licenseNumber: form.dl_license_no,
    });

    if (!validation.valid) {
      alert(validation.message);
      return;
    }

    if (
      validation.normalized &&
      validation.normalized !== form.dl_license_no
    ) {
      handleChange({
        target: {
          name: "dl_license_no",
          value: validation.normalized,
        },
      });
    }

    if (e.target.checkValidity()) {
      nextStep();
    }
  };

  const licenseRule =
    form.dl_country === "Canada"
      ? getCanadianDriverLicenseRule(form.dl_province)
      : null;
  const licenseRuleHelp = licenseRule
    ? `For ${form.dl_province}, the licence number must be ${licenseRule.message}. Example: ${licenseRule.example}.`
    : "";

  return (
    <Container className="mt-5">
      <h4 className="mb-4">Driver License Information</h4>
      <Form className="driver-license-form" onSubmit={handleSubmit}>
        <CountryProvinceSelector
          country={form.dl_country}
          province={form.dl_province}
          countries={countries}
          onChange={(field, value) =>
            handleChange({
              target: {
                name: field === "country" ? "dl_country" : "dl_province",
                value,
              },
            })
          }
        />

        <Row className="mb-3">
          {/* License Number - FULL WIDTH ON MOBILE */}
          <Col xs={12} md={3}>
            <Form.Label>
              License Number <span className="text-danger">*</span>
              {licenseRule && (
                <button
                  type="button"
                  className="btn btn-link p-0 ms-2 align-baseline text-decoration-none"
                  title={licenseRuleHelp}
                  aria-label={licenseRuleHelp}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    verticalAlign: "middle",
                    color: "#0d6efd",
                  }}
                >
                  <LuCircleAlert size={16} />
                </button>
              )}
            </Form.Label>
            <Form.Control
              name="dl_license_no"
              value={form.dl_license_no || ""}
              onChange={(e) =>
                handleChange({
                  target: {
                    name: "dl_license_no",
                    value:
                      form.dl_country === "Canada"
                        ? normalizeCanadianDriverLicenseNumber(e.target.value)
                        : e.target.value,
                  },
                })
              }
              maxLength={licenseRule?.maxLength}
              placeholder={licenseRule?.example || ""}
              required
            />
          </Col>

          {/* Class - STACKS UNDER LICENSE ON MOBILE */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>
              Class <span className="text-danger">*</span>
            </Form.Label>

            {form.dl_country === "Canada" && classes.length > 0 ? (
              <CreatableSelect
                isMulti
                inputValue={classInputValue}
                value={buildSelectedOptions(form.dl_class || [])}
                options={mergeOptions(
                  classes.map((c) => ({
                    value: c.class_code,
                    label: c.description
                      ? `${c.class_code} - ${c.description}`
                      : c.class_code,
                  })),
                  form.dl_class || []
                )}
                onInputChange={(value, meta) => {
                  if (meta.action === "input-change") {
                    setClassInputValue(value);
                  }
                }}
                onKeyDown={handleTokenKeyDown(
                  "dl_class",
                  form.dl_class || [],
                  classInputValue,
                  setClassInputValue
                )}
                onBlur={() =>
                  pushMultiValue(
                    "dl_class",
                    form.dl_class || [],
                    classInputValue,
                    setClassInputValue
                  )
                }
                formatCreateLabel={(value) => `Add "${value}"`}
                onChange={(selected) =>
                  handleChange({
                    target: {
                      name: "dl_class",
                      value: (selected || []).map((s) => s.value),
                    },
                  })
                }
                placeholder="Type values and press comma"
              />
            ) : (
              <Form.Control
                name="dl_class"
                value={classTextValue}
                onChange={(e) => setClassTextValue(e.target.value)}
                onBlur={(e) => commitTextArray("dl_class", e.target.value)}
                placeholder="e.g. 1,3,5"
                required
              />
            )}
          </Col>

          {/* Condition */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>Condition</Form.Label>

            {form.dl_country === "Canada" && conditions.length > 0 ? (
              <CreatableSelect
                isMulti
                inputValue={conditionInputValue}
                value={buildSelectedOptions(form.dl_condition || [])}
                options={mergeOptions(
                  conditions.map((c) => ({
                    value: c.condition_code,
                    label: c.description
                      ? `${c.condition_code} - ${c.description}`
                      : c.condition_code,
                  })),
                  form.dl_condition || []
                )}
                onInputChange={(value, meta) => {
                  if (meta.action === "input-change") {
                    setConditionInputValue(value);
                  }
                }}
                onKeyDown={handleTokenKeyDown(
                  "dl_condition",
                  form.dl_condition || [],
                  conditionInputValue,
                  setConditionInputValue
                )}
                onBlur={() =>
                  pushMultiValue(
                    "dl_condition",
                    form.dl_condition || [],
                    conditionInputValue,
                    setConditionInputValue
                  )
                }
                formatCreateLabel={(value) => `Add "${value}"`}
                onChange={(selected) =>
                  handleChange({
                    target: {
                      name: "dl_condition",
                      value: (selected || []).map((s) => s.value),
                    },
                  })
                }
                placeholder="Type values and press comma"
              />
            ) : (
              <Form.Control
                name="dl_condition"
                value={conditionTextValue}
                onChange={(e) => setConditionTextValue(e.target.value)}
                onBlur={(e) => commitTextArray("dl_condition", e.target.value)}
                placeholder="e.g. A,B,C"
              />
            )}
          </Col>

          {/* Expiry Date */}
          <Col xs={12} md={3} className="mt-3 mt-md-0">
            <Form.Label>
              Expiry Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              name="dl_expdate"
              value={form.dl_expdate || ""}
              onChange={handleChange}
              onPointerDown={handleMobileDatePointerDown}
              required
            />
          </Col>
        </Row>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={prevStep}
          >
            ← Back
          </button>
          <button type="submit" className="btn btn-primary">
            Next →
          </button>
        </div>
      </Form>
    </Container>
  );
};

export default Step3License;
